import { useReactTable } from '@tanstack/react-table';
import { FC, useMemo } from 'react';
import { match } from 'ts-pattern';

import { AvatarWithInitials, LifecycleStatus } from '@eluve/blocks';
import {
  ColDefBuilder,
  DataTableFilterField,
  ExtendedSortingState,
  Icon,
  ServerDataTable,
  Stack,
  Switch,
  TableSearch,
  Tag,
  TagProps,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useDataTableForQuery,
} from '@eluve/components';
import {
  CombinedTenantUsersInvitesOrderBy,
  DbEnums,
} from '@eluve/graphql-types';
import { ResultOf } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  LifecycleStatusCell,
  useIsFeatureFlagEnabled,
} from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';

import { InviteRowActions } from './InviteRowActions';
import { InviteUserModal } from './InviteUserModal';
import { UserRowActions } from './UserRowActions';
import { searchTenantUsersInvitesQuery } from './operations';
import { CombinedRow, CombinedStatusTypesEnum, TableMeta } from './types';
import { buildUsersListFilter, isShowingExcluded } from './usersListFilter';

const defaultSort: ExtendedSortingState<CombinedRow> = [
  { id: 'createdAt', desc: true },
];

// Registered so the hook syncs the toggle to the isExcluded URL param; the
// toggle is rendered as a Switch instead of the standard faceted dropdown.
const filterFields: DataTableFilterField<CombinedRow>[] = [
  {
    id: 'isExcluded',
    label: 'Show excluded users',
    options: [{ label: 'Show excluded users', value: 'true' }],
  },
];

export const UsersList: FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const invitesEnabled = useIsFeatureFlagEnabled('ORGANIZATION_USER_INVITES');
  const { t } = useTranslation();

  const columns = useMemo(
    () =>
      new ColDefBuilder<CombinedRow>()
        .colDef({
          id: 'initials',
          header: '',
          cell: ({ row }) => {
            const hasFullName = row.original.fullName.length > 1;
            if (!hasFullName) {
              return null;
            }

            const { email, fullName, inviteId, userId } = row.original;
            return (
              <AvatarWithInitials
                name={fullName || email}
                id={userId ?? inviteId ?? undefined}
              />
            );
          },
        })
        .defaultSortable('fullName', {
          label: t('tenantAdmin.users.fullNameColumn'),
          cellRenderer: (row) => {
            const hasFullName = row.fullName.length > 1;
            return (
              <Text color={hasFullName ? 'primary' : 'disabled'}>
                {hasFullName ? row.fullName : '—'}
              </Text>
            );
          },
        })
        .defaultSortable('email', t('tenantAdmin.users.emailColumn'))
        .colDef({
          id: 'role',
          header: t('tenantAdmin.users.roleColumn'),
          cell: ({ row }) => (
            <Text>
              {row.original.role === 'tenant-admin'
                ? t('tenantAdmin.users.roleAdmin')
                : t('tenantAdmin.users.roleMember')}
            </Text>
          ),
        })
        .colDef({
          id: 'inviteStatus',
          header: t('tenantAdmin.users.inviteStatusColumn'),
          cell: ({ row }) => {
            // Invite-only states render a tag; accepted user rows (ACTIVE in
            // the view) have no meaningful invite status — their lifecycle
            // state lives in the Status column — so they render a muted dash
            // here.
            const tag = match<
              CombinedStatusTypesEnum,
              { label: string; color: TagProps['color'] } | null
            >(row.original.status)
              .with(DbEnums.OrganizationInviteStatus.PENDING, () => ({
                color: 'orange',
                label: t('tenantAdmin.users.invitePendingTag'),
              }))
              .with(DbEnums.OrganizationInviteStatus.REVOKED, () => ({
                color: 'negative',
                label: t('tenantAdmin.users.inviteRevokedTag'),
              }))
              .with(DbEnums.OrganizationInviteStatus.EXPIRED, () => ({
                color: 'gray',
                label: t('tenantAdmin.users.inviteExpiredTag'),
              }))
              .otherwise(() => null);
            return tag ? (
              <Tag color={tag.color} label={tag.label} size="s" />
            ) : (
              <Text color="disabled">—</Text>
            );
          },
        })
        .colDef({
          id: 'lifecycleStatus',
          header: t('tenantAdmin.users.statusColumn'),
          cell: ({ row, table }) => {
            const { userId, lifecycleStatus, isExcluded, email } = row.original;
            const meta = table.options.meta as TableMeta;
            return (
              <LifecycleStatusCell
                tenantId={meta.tenantId}
                userId={userId}
                status={lifecycleStatus}
                isExcluded={isExcluded}
                subjectLabel={email}
                onChanged={meta.refetch}
              />
            );
          },
        })
        .dateSortable('lastAccessedAt', t('tenantAdmin.users.lastSeenColumn'))
        .dateSortable('createdAt', t('tenantAdmin.users.joinInviteDateColumn'))
        .colDef({
          accessorKey: 'actions',
          header: '',
          cell: ({ row, table }) => {
            if (row.original.userId) {
              return (
                <UserRowActions
                  userId={row.original.userId}
                  email={row.original.email}
                  refetch={(table.options.meta as TableMeta).refetch}
                  role={row.original.role}
                  isExcluded={row.original.isExcluded}
                  onExcluded={() => {
                    // On page 1 just refresh in place; on later pages reset to
                    // page 1 and let the pagination change drive the single
                    // refetch (avoids racing an out-of-range request).
                    if (table.getState().pagination.pageIndex === 0) {
                      (table.options.meta as TableMeta).refetch();
                    } else {
                      table.setPageIndex(0);
                    }
                  }}
                />
              );
            } else if (row.original.inviteId) {
              return (
                <InviteRowActions
                  inviteId={row.original.inviteId}
                  email={row.original.email}
                  refetch={(table.options.meta as TableMeta).refetch}
                  status={row.original.status}
                />
              );
            }
          },
        })
        .build(),
    [t],
  );

  const convertToRows = (
    results: ResultOf<typeof searchTenantUsersInvitesQuery>,
  ): CombinedRow[] => {
    let combinedTenantUsersInvites = results.combinedTenantUsersInvites;
    if (!invitesEnabled) {
      combinedTenantUsersInvites = combinedTenantUsersInvites.filter(
        (item) => item.inviteId == null,
      );
    }
    return combinedTenantUsersInvites.map((userOrInvite) => ({
      inviteId: userOrInvite.inviteId,
      userId: userOrInvite.userId,
      firstName: userOrInvite.firstName ?? '',
      lastName: userOrInvite.lastName ?? '',
      fullName: formatHumanName(
        userOrInvite.firstName ?? undefined,
        userOrInvite.lastName ?? undefined,
      ),
      email: userOrInvite.email ?? '',
      role: userOrInvite.role ?? 'tenant-member',
      status: userOrInvite.isExpired
        ? DbEnums.OrganizationInviteStatus.EXPIRED
        : (userOrInvite.status as CombinedStatusTypesEnum),
      lifecycleStatus:
        (userOrInvite.lifecycleStatus as LifecycleStatus | null) ?? null,
      lastAccessedAt: userOrInvite.lastAccessedAt,
      createdAt: userOrInvite.createdAt,
      isExcluded: userOrInvite.isExcluded ?? false,
    }));
  };

  const {
    data,
    filters,
    isPending,
    reactTableOptions,
    refetch,
    rows,
    search,
    setSearch,
  } = useDataTableForQuery({
    query: searchTenantUsersInvitesQuery,
    defaultSort,
    filterFields,
    convertToRows,
    convertSearchParamsToVariables: ({
      offset,
      limit,
      search,
      sorting,
      filters,
    }) => {
      return {
        filter: buildUsersListFilter({
          tenantId,
          search,
          showExcluded: isShowingExcluded(filters),
        }),
        limit,
        offset,
        orderBy:
          sorting && sorting.length
            ? sorting.map(({ desc, id }) => ({
                ...match<keyof CombinedRow, CombinedTenantUsersInvitesOrderBy>(
                  id,
                )
                  .with('fullName', () => ({
                    firstName: desc ? 'DESC' : 'ASC',
                    lastName: desc ? 'DESC' : 'ASC',
                  }))
                  .otherwise(() => ({
                    [id]: desc ? 'DESC_NULLS_LAST' : 'ASC',
                  })),
              }))
            : undefined,
      };
    },
  });

  const rowCount =
    data?.combinedTenantUsersInvitesAggregate?.aggregate?.count ?? 0;

  const table = useReactTable({
    data: rows,
    columns,
    rowCount,
    meta: { refetch, tenantId },
    ...reactTableOptions,
  });

  const showExcluded = isShowingExcluded(filters);

  const onShowExcludedChange = (checked: boolean) => {
    table.setColumnFilters(
      checked ? [{ id: 'isExcluded', value: ['true'] }] : [],
    );
  };

  return (
    <Stack gap={6} align="stretch">
      <Stack justify="between" gap={2} direction="horizontal">
        <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
          <Switch
            id="show-excluded-users"
            checked={showExcluded}
            onCheckedChange={onShowExcludedChange}
            aria-label={t('tenantAdmin.users.showExcludedUsers')}
          />
          <label htmlFor="show-excluded-users" className="cursor-pointer">
            <Text size="sm">{t('tenantAdmin.users.showExcludedUsers')}</Text>
          </label>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Icon name="Info" size="sm" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t('tenantAdmin.users.excludedUsersTooltip')}
            </TooltipContent>
          </Tooltip>
        </Stack>
        <FeatureFlaggedComponent flag="ORGANIZATION_USER_INVITES">
          <InviteUserModal onSuccess={() => refetch()} />
        </FeatureFlaggedComponent>
      </Stack>
      <Stack className="rounded-md border border-border-primary">
        <TableSearch
          onChange={setSearch}
          placeholder={t('tenantAdmin.users.searchPlaceholder')}
          value={search ?? ''}
        />
        <ServerDataTable<CombinedRow> isPending={isPending} table={table} />
      </Stack>
    </Stack>
  );
};
